import { useState, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import {
  Typography,
  FormControl,
  Backdrop,
  LinearProgress,
} from "@mui/material"
import Box from "@mui/material/Box"

import { refinementModule } from "@/gluelayer/core/modules/refinement"
import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import { fetchRefinementZipFile } from "@/core/app/slices/clinical/clinicalThunkApi"

interface QuickRetouchCaseDlgProps {
  open: boolean
  onClose: () => void
  onRunCopyPlan: (casePath: string) => void
}
import { caseManagement, gdsPlusSetup } from "@/gluelayer"
import { fromPromise } from "xstate/lib/behaviors"
import { set } from "react-hook-form"

let curFolderNames = []

export const QuickRetouchCaseDlg = ({
  open,
  onClose,
  onRunCopyPlan,
}: QuickRetouchCaseDlgProps) => {
  
  const [casePath, setCasePath] = useState<string>("")
  const [historyList, setHistoryList] = useState([
    { name: "A", date: "2023-10-01" },
    { name: "B", date: "2023-10-02" },
    { name: "C", date: "2023-10-03" },
  ])
  const { caseId, patientId } = useParams()
  const [currentRefinement, setCurrentRefinement] = useState({
    name: "",
    date: "",
  })
  const [lastRefinement, setLastRefinement] = useState({
    name: "",
    date: "",
  })

  const [showDownload, setShowDownload] = useState(false)
  const [downloadMsg, setDownloadMsg] = useState("")
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService) // current case
  const { refinementZips } = useAppSelector(
    (state: RootState) => state.clinicalService,
  )
  const dispatch = useAppDispatch()

  const createDate = caseDetail.createDate
    ? `${caseDetail.createDate.substring(
        5,
        7,
      )}/${caseDetail.createDate.substring(
        8,
        10,
      )}/${caseDetail.createDate.substring(2, 4)}`
    : ""

  useEffect(() => {
    // Create hisotry list
    const refinementHistoryList = refinementModule.getRefinementDateInfo()
    const firstKey = Object.keys(refinementHistoryList)[0]
    if (firstKey && refinementHistoryList[firstKey] !== "") {
      setCurrentRefinement({
        name: firstKey,
        date: refinementHistoryList[firstKey],
      })
      setLastRefinement({
        name: firstKey,
        date: refinementHistoryList[firstKey],
      })
    } else {
      setCurrentRefinement({ name: "initial plan", date: createDate })
      setLastRefinement({ name: "initial plan", date: createDate })
    }
    const tempList = []
    for (const item in refinementHistoryList) {
      if (item !== "original")
        tempList.push({
          name: item,
          date:
            refinementHistoryList[item] !== ""
              ? refinementHistoryList[item]
              : createDate,
        })
    }
    tempList.push({ name: "initial plan", date: createDate })
    setHistoryList([...tempList])
    return () => {
      setShowDownload(false)
    }
  }, [])

  useEffect(() => {
    setShowDownload(false)
    if (Object.keys(refinementZips).length > 0 && curFolderNames.length > 0 && open) {
      caseManagement
        .AddARefinementHistory(
          curFolderNames,
          refinementZips as Record<string, Blob>,
        )
        .then(() => {
          setDownloadMsg("Download completed ...")
          runRetouchV2CopyPlan(curFolderNames[0])
        })
    }
  }, [refinementZips])

  const onClickHitostryItem = (name, date) => {
    const wasmName = name === "initial plan" ? "original" : name
    const folderNames = refinementModule.getDownloadDataNames(wasmName)
    let refinementFiles = []
    let tmpfolderNames = []
    for (let j = 0; j < folderNames.size(); j++) {
      const folderName = folderNames.get(j)
      const isExisted = caseManagement.IsRefinementItemExisted(folderName)
      if (!isExisted) {
        tmpfolderNames.push(folderName)
        refinementFiles.push(folderName + ".zip")
      }
    }
    if (refinementFiles.length > 0) {
      console.log("refinementzips dispatch")
      setShowDownload(true)
      setDownloadMsg("Downloading files...")
      curFolderNames = tmpfolderNames
      dispatch(
        fetchRefinementZipFile({
          orgId: "",
          patientId,
          caseId,
          refinementFiles,
        }),
      )
    } else {
      runRetouchV2CopyPlan(folderNames.get(0))
    }
  }

  const runRetouchV2CopyPlan = (histPath: string) => {
    console.log("runRetouchV2CopyPlan, ", histPath)
    setShowDownload(true)
    setDownloadMsg("Running Copy Plan...")
    setTimeout(() => {
      gdsPlusSetup.RunRetouchV2CopyPlan(histPath)
      setShowDownload(false)
      onClose() // Close the dialog after running the copy plan
    }, 100)
  }

  const HistoryItem = ({ id, name, date }) => {
    return (
      <Box sx={{ display: "flex" }}>
        <Typography
          variant="caption"
          sx={{
            // color: name === lastRefinement.name ? "#9C9C9C" : "#000",
            fontFamily: "Poppins",
            fontSize: "14px",
            fontWeight: "400",
            letterSpacing: "0.46px",
            marginTop: "7px",
            marginBottom: "7px",
            marginLeft: "15px",
            display: "inline-block",
            width: "auto",
            "&:hover": {
              cursor: "pointer",
            },
          }}
          onClick={async () => {
            await new Promise((resolve) => setTimeout(resolve, 0))
            onClickHitostryItem(name, date)
          }}
        >
          {` #` + id + " | " + name + " - " + date}
        </Typography>
      </Box>
    )
  }
  const HistoryListForm = () => {
    return (
      <FormControl>
        {historyList.map((item, index) => {
          return (
            <HistoryItem
              key={"history-item-" + index}
              id={caseId}
              name={item.name}
              date={item.date}
            />
          )
        })}
      </FormControl>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy Plan from History Case</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>Hisotry Cases</DialogContentText>
        <HistoryListForm />
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={showDownload}
        >
          <Box sx={{ width: "40%" }}>
            {downloadMsg}
            <LinearProgress />
          </Box>
        </Backdrop>
      </DialogContent>
      <DialogActions
        sx={{ p: 3 }} // Default justifyContent is 'flex-end', flexDirection is 'row'
      >
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          sx={{ mr: 1 }} // Add some margin to the right of the Cancel button
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onRunCopyPlan(casePath) // Pass the actual casePath from the state
          }}
          variant="contained"
          disabled={!casePath}
        >
          Run Copy Plan
        </Button>
      </DialogActions>
    </Dialog>
  )
}
